import React, { useEffect, useState, Component } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToProductReviews, findPurchaseOrderId, getUserReviewForProduct, submitReview, type Review } from '../../../utils/firebase';
function StarRatingInput({
  rating,
  onChange
}: {
  rating: number;
  onChange: (r: number) => void;
}) {
  const [hover, setHover] = useState(0);
  return <div className="flex items-center gap-1" data-id="element-2312">
      {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => onChange(star)} onMouseEnter={() => setHover(star)} onMouseLeave={() => setHover(0)} className="p-1 -m-1 transition-colors" data-id="element-2313">
          <Star className={`w-6 h-6 ${star <= (hover || rating) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2314" />
        </button>)}
    </div>;
}
function ReviewForm({
  productId,
  orderId,
  existingReview,
  onSuccess,
  onCancel
}: {
  productId: string;
  orderId: string;
  existingReview: Review | null;
  onSuccess: () => void;
  onCancel?: () => void;
}) {
  const {
    user,
    profile
  } = useAuth();
  const [rating, setRating] = useState(existingReview?.rating ?? 5);
  const [title, setTitle] = useState(existingReview?.title ?? '');
  const [body, setBody] = useState(existingReview?.body ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (rating < 1 || rating > 5) {
      setError('Please select a star rating.');
      return;
    }
    if (!title.trim() || !body.trim()) {
      setError('Please provide a title and review text.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await submitReview({
        productId,
        userUid: user.uid,
        displayName: profile?.displayName || user.displayName || 'Customer',
        rating,
        title: title.trim(),
        body: body.trim(),
        orderId
      });
      onSuccess();
    } catch (err: any) {
      setError(err?.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="bg-[#F9F9F9] border border-[#E5E5E5] p-6 md:p-8 rounded-xl" data-id="element-2315">
      <h3 className="text-xl font-bold text-[#1F1F1F] mb-6" data-id="element-2316">
        {existingReview ? 'Edit Your Review' : 'Write a Review'}
      </h3>

      <div className="space-y-5" data-id="element-2317">
        <div data-id="element-2318">
          <label className="block text-sm font-bold text-[#1F1F1F] mb-2" data-id="element-2319">
            Overall Rating
          </label>
          <StarRatingInput rating={rating} onChange={setRating} data-id="element-2320" />
        </div>

        <div data-id="element-2321">
          <label className="block text-sm font-bold text-[#1F1F1F] mb-2" data-id="element-2322">
            Review Title
          </label>
          <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder="Summarize your experience" className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2323" />
        </div>

        <div data-id="element-2324">
          <label className="block text-sm font-bold text-[#1F1F1F] mb-2" data-id="element-2325">
            Review
          </label>
          <textarea required rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="What did you like or dislike? How did you use it?" className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-y" data-id="element-2326" />
        </div>

        {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-2327">
            <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-2328" />
            <p className="text-xs text-burgundy leading-relaxed" data-id="element-2329">{error}</p>
          </div>}

        <div className="flex items-center gap-3 pt-2" data-id="element-2330">
          <button type="submit" disabled={isSubmitting} className="px-6 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center min-w-[140px]" data-id="element-2331">
            {isSubmitting ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2332" /> : 'Submit Review'}
          </button>
          {onCancel && <button type="button" onClick={onCancel} disabled={isSubmitting} className="px-6 py-3 bg-white border border-[#E5E5E5] text-[#1F1F1F] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md disabled:opacity-60" data-id="element-2333">
              Cancel
            </button>}
        </div>
      </div>
    </form>;
}
export function ProductReviews({
  productId
}: {
  productId: string;
}) {
  const {
    user
  } = useAuth();
  const location = useLocation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [eligibleOrderId, setEligibleOrderId] = useState<string | null>(null);
  const [userReview, setUserReview] = useState<Review | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingEligibility, setLoadingEligibility] = useState(true);
  useEffect(() => {
    const unsub = subscribeToProductReviews(productId, setReviews);
    return unsub;
  }, [productId]);
  useEffect(() => {
    if (!user) {
      setEligibleOrderId(null);
      setUserReview(null);
      setLoadingEligibility(false);
      return;
    }
    let isMounted = true;
    setLoadingEligibility(true);
    Promise.all([findPurchaseOrderId(user.uid, productId), getUserReviewForProduct(productId, user.uid)]).then(([orderId, review]) => {
      if (!isMounted) return;
      setEligibleOrderId(orderId);
      setUserReview(review);
    }).catch(err => {
      console.error('Failed to fetch eligibility:', err);
    }).finally(() => {
      if (isMounted) setLoadingEligibility(false);
    });
    return () => {
      isMounted = false;
    };
  }, [user, productId]);
  const handleSuccess = () => {
    setIsEditing(false);
    // Re-fetch the user's review to update local state
    if (user) {
      getUserReviewForProduct(productId, user.uid).then(setUserReview);
    }
  };
  return <section id="reviews" className="py-16 md:py-24 border-t border-[#E5E5E5]" data-id="element-2334">
      <div className="max-w-3xl mx-auto px-6" data-id="element-2335">
        <h2 className="text-3xl font-bold text-[#1F1F1F] mb-10" data-id="element-2336">
          Customer Reviews
        </h2>

        {/* Review Form / Gate */}
        <div className="mb-16" data-id="element-2337">
          {loadingEligibility ? <div className="h-32 bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl animate-pulse" data-id="element-2338" /> : !user ? <div className="bg-[#F9F9F9] border border-[#E5E5E5] p-8 rounded-xl text-center" data-id="element-2339">
              <h3 className="text-lg font-bold text-[#1F1F1F] mb-2" data-id="element-2340">
                Have you tried this product?
              </h3>
              <p className="text-[#5A5A5A] mb-6" data-id="element-2341">
                Sign in to share your experience with others.
              </p>
              <Link to={`/login?from=${encodeURIComponent(location.pathname + location.search + location.hash)}`} className="inline-block px-6 py-3 bg-[#1F1F1F] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors rounded" data-id="element-2342">
                Sign In to Review
              </Link>
            </div> : !eligibleOrderId ? <div className="bg-[#F9F9F9] border border-[#E5E5E5] p-8 rounded-xl text-center" data-id="element-2343">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-2344">
                <Lock className="w-5 h-5 text-[#9A9A90]" data-id="element-2345" />
              </div>
              <h3 className="text-lg font-bold text-[#1F1F1F] mb-2" data-id="element-2346">
                Verified Purchasers Only
              </h3>
              <p className="text-[#5A5A5A]" data-id="element-2347">
                You must purchase this product before leaving a review.
              </p>
            </div> : userReview && !isEditing ? <div className="bg-[#F9F9F9] border border-[#E5E5E5] p-6 rounded-xl flex items-center justify-between gap-4 flex-wrap" data-id="element-2348">
              <div data-id="element-2349">
                <h3 className="text-base font-bold text-[#1F1F1F] mb-1" data-id="element-2350">
                  You've reviewed this product
                </h3>
                <p className="text-sm text-[#5A5A5A]" data-id="element-2351">
                  Thanks for sharing your experience!
                </p>
              </div>
              <button onClick={() => setIsEditing(true)} className="px-5 py-2.5 bg-white border border-[#E5E5E5] text-[#1F1F1F] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded shadow-sm" data-id="element-2352">
                Edit Review
              </button>
            </div> : <ReviewForm productId={productId} orderId={eligibleOrderId} existingReview={userReview} onSuccess={handleSuccess} onCancel={userReview ? () => setIsEditing(false) : undefined} data-id="element-2353" />}
        </div>

        {/* Reviews List */}
        <div className="space-y-8" data-id="element-2354">
          {reviews.length === 0 ? <div className="text-center py-12 border-t border-[#E5E5E5]" data-id="element-2355">
              <p className="text-[#5A5A5A]" data-id="element-2356">
                No reviews yet. Be the first to share your experience!
              </p>
            </div> : reviews.map(review => <article key={review.id} className="border-t border-[#E5E5E5] pt-8 first:border-0 first:pt-0" data-id="element-2357">
                <div className="flex items-start justify-between gap-4 mb-3" data-id="element-2358">
                  <div data-id="element-2359">
                    <div className="flex items-center gap-1 mb-2" data-id="element-2360">
                      {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2361" />)}
                    </div>
                    <h4 className="text-lg font-bold text-[#1F1F1F]" data-id="element-2362">
                      {review.title}
                    </h4>
                  </div>
                  <span className="text-sm text-[#9A9A90] whitespace-nowrap" data-id="element-2363">
                    {new Date(review.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-4" data-id="element-2364">
                  <span className="text-sm font-medium text-[#1F1F1F]" data-id="element-2365">
                    {review.displayName}
                  </span>
                  {review.verifiedPurchase && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1F73B7] bg-[#1F73B7]/10 px-2 py-0.5 rounded-sm" data-id="element-2366">
                      <CheckCircle2 className="w-3 h-3" data-id="element-2367" />
                      Verified Buyer
                    </span>}
                </div>

                <p className="text-base text-[#3B3B3B] leading-relaxed whitespace-pre-wrap" data-id="element-2368">
                  {review.body}
                </p>
              </article>)}
        </div>
      </div>
    </section>;
}